import React from "react";
import {
  Timeline,
  Text,
  Paper,
  UnstyledButton,
  Group,
  ThemeIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  IconCheck,
  IconX,
  IconClock,
  IconCircleDot,
} from "@tabler/icons";
import moment from "moment";
import McaStatusBadge from "./McaStatusBadge";

const getStepColor = (step) => {
  if (step.status === "APPROVED") return "green";
  if (step.status === "REJECTED") return "red";
  return "orange";
};

const getStepBullet = (step, idx, currentStep, requestStatus) => {
  if (step.status === "APPROVED") {
    return (
      <ThemeIcon color="green" radius="xl" size={24}>
        <IconCheck size={14} />
      </ThemeIcon>
    );
  }
  if (step.status === "REJECTED") {
    return (
      <ThemeIcon color="red" radius="xl" size={24}>
        <IconX size={14} />
      </ThemeIcon>
    );
  }
  if (
    idx === currentStep &&
    ["PENDING", "IN_PROGRESS"].includes(requestStatus)
  ) {
    return (
      <ThemeIcon color="orange" radius="xl" size={24}>
        <IconCircleDot size={14} />
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon color="orange" radius="xl" size={24}>
      <IconClock size={14} />
    </ThemeIcon>
  );
};

const McaTimeline = ({
  request,
  selectedStep = 0,
  onSelectStep,
  plain = false,
  showSubmission = false,
}) => {
  const { t } = useTranslation();
  const steps = request?.stepResults ?? [];
  const currentStep = request?.currentStep ?? 0;
  const requestStatus = request?.status;
  const makerName = request?.maker?.name || request?.maker?.cname || "";
  const submittedAt = request?.createdAt
    ? moment(request.createdAt).format("YYYY-MM-DD HH:mm")
    : "";

  if (!steps.length) {
    return <Text color="dimmed">No approval steps configured.</Text>;
  }

  const timeline = (
    <Timeline
      active={steps.length + (showSubmission ? 1 : 0)}
      bulletSize={24}
      lineWidth={2}
    >
      {showSubmission ? (
        <Timeline.Item
          bullet={
            <ThemeIcon color="green" radius="xl" size={24}>
              <IconCheck size={14} />
            </ThemeIcon>
          }
          color="green"
          title={
            <>
              <Text size="sm" weight={600}>
                {t("Submitted for approval")}
              </Text>
              <Text size="xs" color="dimmed" mt={2}>
                {[makerName, submittedAt].filter(Boolean).join(" · ") || t("Submitted")}
              </Text>
            </>
          }
        />
      ) : null}
      {steps.map((step, idx) => {
        const isSelected = typeof onSelectStep === "function" && selectedStep === idx;
        const color = getStepColor(step);
        const showActionTimestamp =
          step.actionAt &&
          (step.status === "APPROVED" || step.status === "REJECTED");
        const waitingNames = Array.isArray(step.approverNames)
          ? step.approverNames.filter(Boolean)
          : [];
        const description = showActionTimestamp
          ? `${step.actionBy?.name ?? step.actionBy?.cname ?? "Approver"} · ${moment(step.actionAt).format("YYYY-MM-DD HH:mm")}`
          : idx === currentStep &&
              ["PENDING", "IN_PROGRESS"].includes(requestStatus)
            ? waitingNames.length
              ? `Awaiting approval from ${waitingNames.join(", ")}`
              : "Awaiting approval"
            : "Pending";
        const body = (
          <>
            <Text size="sm" weight={isSelected ? 700 : 600}>
              {step.name ?? `Step ${idx + 1}`}
            </Text>
            <Text size="xs" color="dimmed" mt={2}>
              {description}
            </Text>
            <Group mt={4} spacing="xs">
              <McaStatusBadge status={step.status} />
            </Group>
          </>
        );

        return (
          <Timeline.Item
            key={idx}
            bullet={getStepBullet(step, idx, currentStep, requestStatus)}
            color={color}
            title={
              plain ? (
                body
              ) : (
                <UnstyledButton
                  onClick={() => onSelectStep?.(idx)}
                  style={{ width: "100%", textAlign: "left" }}
                >
                  <Paper
                    p="xs"
                    radius="sm"
                    withBorder={isSelected}
                    sx={(theme) =>
                      isSelected
                        ? {
                            borderColor: theme.colors.teal[6],
                            backgroundColor:
                              theme.colorScheme === "dark"
                                ? theme.colors.dark[6]
                                : theme.colors.teal[0],
                          }
                        : {}
                    }
                  >
                    {body}
                  </Paper>
                </UnstyledButton>
              )
            }
          />
        );
      })}
    </Timeline>
  );

  if (plain) return timeline;

  return (
    <Paper withBorder p="md" radius="sm">
      {timeline}
    </Paper>
  );
};

export default McaTimeline;
